import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface LoadingConfig {
  timeoutDuration: number; // in milliseconds
  showTimeoutNotification: boolean;
  timeoutMessage: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  // Centralized configuration - change timeout here for all components
  private readonly config: LoadingConfig = {
    timeoutDuration: 30000, // 30 seconds
    showTimeoutNotification: true,
    timeoutMessage: 'Request timed out. Please try again.'
  };

  private loadingSubject = new BehaviorSubject<boolean>(false);
  private activeRequests = 0;
  private loadingTimeout: any = null;

  // Observable for components to subscribe to
  isLoading$: Observable<boolean> = this.loadingSubject.asObservable();

  // Get current loading state
  get isLoading(): boolean {
    return this.loadingSubject.value;
  }

  // Get timeout duration (for external use if needed)
  get timeoutDuration(): number {
    return this.config.timeoutDuration;
  }

  // Get timeout message
  get timeoutMessage(): string {
    return this.config.timeoutMessage;
  }

  // Start loading with automatic timeout
  startLoading(): void {
    this.activeRequests++;
    if (this.activeRequests === 1) {
      this.loadingSubject.next(true);
      this.startTimeout();
    }
  }

  // Stop loading
  stopLoading(): void {
    this.activeRequests = Math.max(0, this.activeRequests - 1);
    if (this.activeRequests === 0) {
      this.loadingSubject.next(false);
      this.clearTimeout();
    }
  }

  // Force stop all loading (useful for navigation or errors)
  forceStopLoading(): void {
    this.activeRequests = 0;
    this.loadingSubject.next(false);
    this.clearTimeout();
  }

  // Check if timeout occurred
  private startTimeout(): void {
    this.clearTimeout();
    this.loadingTimeout = setTimeout(() => {
      if (this.loadingSubject.value) {
        console.error('Loading timeout: Request exceeded', this.config.timeoutDuration / 1000, 'seconds');
        this.forceStopLoading();
      }
    }, this.config.timeoutDuration);
  }

  private clearTimeout(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
  }
}
